import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, appUserAuth } from "../lib/api";
import { Lock, UserCirclePlus, ArrowRight, ShieldCheck, Gift, Key } from "@phosphor-icons/react";
import { toast } from "sonner";
import WelcomeFit from "./WelcomeFit";
import { WELCOME_FLAG } from "../lib/progress";
import { gateEvent, OPEN_GATE_EVENT } from "../lib/gate";

/**
 * AuthGate — wraps the entire client-facing app.
 *
 * Behavior:
 *   • If the user is already authenticated for this session → render children.
 *   • Else, if the device has a remembered account → PIN entry screen (every visit).
 *   • Else → public site; gated tools are viewable but any interaction opens sign-up.
 *
 * Bypass paths (do NOT gate these — needed for App Store review + advisors):
 *   • /login (advisor portal)
 *   • /privacy (App Store required)
 *   • /credentials (public advisor credentials page)
 */
const BYPASS_PATHS = ["/login", "/privacy", "/support", "/credentials", "/r/", "/rollover/s/"];
// Public to view; any interaction requires a free account.
const GATED_PATHS = ["/quiz", "/calculator", "/reserve", "/clarity-score", "/tax-buckets", "/check-in", "/goals", "/results"];
// Guests can use the inputs here; results stay blurred (LockedBlur) and [data-gate-require] actions open sign-up.
const TEASER_PATHS = ["/calculator", "/clarity-score"];
const INTERACTIVE = "button, input, select, textarea, label, [role=button], [role=slider], [role=radio], [role=checkbox], [role=combobox], [role=switch], [role=tab]";
const ALLOW_ZONES = "header, nav, footer, [data-gate-allow]";

export default function AuthGate({ children }) {
  const loc = useLocation();
  const bypass = BYPASS_PATHS.some((p) => loc.pathname.startsWith(p)) || loc.pathname.startsWith("/dashboard");
  const gated = GATED_PATHS.find((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`));

  const [, setTick] = useState(0);
  const authed = appUserAuth.isLoggedIn();
  const remembered = appUserAuth.hasAccountOnDevice();

  const rerender = () => setTick((t) => t + 1);

  if (bypass) return children;
  if (authed) {
    return (
      <>
        {children}
        {localStorage.getItem(WELCOME_FLAG) && <WelcomeFit onClose={rerender} />}
      </>
    );
  }
  if (!remembered) {
    return (
      <>
        {children}
        {gated && <GuestGate key={gated} tool={gated.slice(1)} teaser={TEASER_PATHS.includes(gated)} onDone={rerender} />}
      </>
    );
  }

  return (
    <div data-testid="app-auth-gate" className="fixed inset-0 z-[100] bg-exodus-bg text-exodus-text overflow-y-auto">
      <div className="min-h-screen flex items-center justify-center p-6">
        <PinScreen onDone={rerender} />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Guest gate — page is visible; interaction (or results) opens sign-up    */
/* ═══════════════════════════════════════════════════════════════════════ */
function GuestGate({ tool, teaser, onDone }) {
  const navigate = useNavigate();
  const [open, setOpenRaw] = useState(false);
  const setOpen = (v) => { setOpenRaw(v); if (v) gateEvent("open", tool); };

  useEffect(() => {
    if (tool === "check-in") setOpen(true);
    const intercept = (e) => {
      const t = e.target;
      if (!(t instanceof Element) || t.closest("[data-gate-ui]") || t.closest(ALLOW_ZONES)) return;
      if (!t.closest(teaser ? "[data-gate-require]" : INTERACTIVE)) return;
      e.preventDefault();
      e.stopPropagation();
      if ((e.type === "pointerdown" || e.type === "mousedown") && !t.closest("input[type=range], [role=slider]")) return;
      if (e.type === "focusin" && typeof t.blur === "function") t.blur();
      setOpen(true);
    };
    const onOpen = () => setOpen(true);
    const evts = ["pointerdown", "mousedown", "click", "focusin", "input", "change"];
    evts.forEach((ev) => document.addEventListener(ev, intercept, { capture: true, passive: false }));
    window.addEventListener(OPEN_GATE_EVENT, onOpen);
    return () => {
      evts.forEach((ev) => document.removeEventListener(ev, intercept, { capture: true }));
      window.removeEventListener(OPEN_GATE_EVENT, onOpen);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const notNow = () => { setOpenRaw(false); navigate("/"); };
  const done = (isNew) => { if (isNew === true) gateEvent("complete", tool); onDone(); };

  return (
    <div data-gate-ui>
      {!open && (
        <div data-testid="guest-preview-bar" className="fixed bottom-20 sm:bottom-4 left-3 right-3 sm:left-1/2 sm:right-auto sm:-translate-x-1/2 z-[90] card-surface px-4 sm:px-5 py-3 flex items-center justify-between gap-3 sm:gap-4 shadow-2xl sm:max-w-[92vw]">
          <p className="text-xs sm:text-sm text-exodus-text/80">
            {teaser ? "Preview mode. Try it out. Your results unlock with free access." : "Preview mode. Create free access to use this tool and save your results."}
          </p>
          <button data-testid="guest-preview-signup" onClick={() => setOpen(true)} className="btn-gold text-xs shrink-0">Create access</button>
        </div>
      )}
      {open && (
        <div data-testid="guest-gate-modal" className="fixed inset-0 z-[100] bg-exodus-bg/90 backdrop-blur-md overflow-y-auto">
          <div className="min-h-screen flex flex-col items-center justify-center p-6">
            <SignUpScreen onDone={done} fromGate />
            <button data-testid="guest-gate-not-now" onClick={notNow} className="mt-5 text-sm text-exodus-muted hover:text-exodus-gold py-2 px-4">
              Not now, take me to the homepage
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Sign-up screen — first-time account creation                            */
/* ═══════════════════════════════════════════════════════════════════════ */
function SignUpScreen({ onDone, fromGate = false }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("signup"); // 'signup' | 'existing-pin'
  const [existingName, setExistingName] = useState("");
  const referrer = useReferrer();

  if (mode === "reset") return <ResetPin initialEmail={email} onDone={onDone} onCancel={() => setMode("existing-pin")} />;

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "signup") {
      if (!email || !fullName.trim()) return toast.error("Enter your email and full name.");
      if (pin.length !== 4 || !/^\d{4}$/.test(pin)) return toast.error("PIN must be 4 digits.");
      if (pin !== pinConfirm) return toast.error("PINs don't match.");
      setLoading(true);
      try {
        const r = await api.post("/app/register", { email, full_name: fullName, pin });
        appUserAuth.activate(r.data.token, r.data.user.email, r.data.user.full_name);
        if (!fromGate) localStorage.setItem(WELCOME_FLAG, "1");
        attributeReferral();
        toast.success(`Welcome, ${r.data.user.full_name.split(" ")[0]}.`);
        onDone(true);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const url = err?.config ? `${err.config.baseURL || ""}${err.config.url || ""}` : "unknown";
        console.error("[EXODUS] register failed:", { status, detail, url, err });
        if (status === 409) {
          // Account exists → flip into PIN mode with this email.
          try {
            const l = await api.get(`/app/lookup?email=${encodeURIComponent(email)}`);
            setExistingName(l.data.full_name || "");
          } catch { /* ignore lookup fail */ }
          setMode("existing-pin");
          setPin(""); setPinConfirm("");
          toast.info("An account already exists for that email. Enter your PIN.");
        } else if (!err.response) {
          // Network / CORS / DNS failure — no HTTP status received.
          toast.error("Can't reach the server. Check your internet connection and try again.");
        } else if (status === 404) {
          toast.error("Signup service isn't available yet. Please try again in a moment.");
        } else if (status >= 500) {
          toast.error("Server issue on our end. Try again — we're on it.");
        } else if (detail) {
          toast.error(detail);
        } else {
          toast.error(`Sign-up failed (${status}). Please try again.`);
        }
      } finally {
        setLoading(false);
      }
    } else {
      // existing-pin
      if (pin.length !== 4) return toast.error("PIN must be 4 digits.");
      setLoading(true);
      try {
        const r = await api.post("/app/pin-login", { email, pin });
        appUserAuth.activate(r.data.token, r.data.user.email, r.data.user.full_name);
        toast.success(`Welcome back, ${r.data.user.full_name.split(" ")[0]}.`);
        onDone();
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        console.error("[EXODUS] pin login failed:", { status, detail, err });
        if (!err.response) {
          toast.error("Can't reach the server. Check your internet.");
        } else if (status === 404) {
          toast.error("This account isn't set up here yet. Try signing up.");
        } else {
          toast.error(detail || "Incorrect PIN.");
        }
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <form onSubmit={submit} autoComplete="off" data-testid="app-signup-form" className="card-surface p-10 w-full max-w-md">
      <UserCirclePlus size={36} weight="thin" className="text-exodus-gold mb-5" />
      <p className="eyebrow">EXODUS Life &amp; Wealth</p>
      {mode === "signup" && referrer && (
        <p data-testid="app-signup-referrer" className="mt-4 inline-flex items-center gap-2 text-sm border border-exodus-gold/50 text-exodus-gold px-3 py-1.5">
          <Gift size={16} /> {referrer} invited you to EXODUS
        </p>
      )}
      <h1 data-testid="app-signup-title" className="font-display text-3xl mt-3 leading-tight">
        {mode === "signup" ? "Create your access." : `Welcome back${existingName ? `, ${existingName.split(" ")[0]}` : ""}.`}
      </h1>
      <p className="text-sm text-exodus-text/70 mt-3 leading-relaxed">
        {mode === "signup"
          ? "Free access to the calculator, quiz, audio playbooks, and Wealth for Life Blueprint. Set a 4-digit PIN so you can unlock the app in seconds next time."
          : "Enter the PIN you set when you created this account."}
      </p>

      {/* Decoy fields — absorb iOS keychain autofill */}
      <input type="text" name="username_decoy" autoComplete="username" style={{ display: "none" }} />
      <input type="password" name="password_decoy" autoComplete="current-password" style={{ display: "none" }} />

      <div className="mt-8 space-y-4">
        <input
          data-testid="app-signup-email"
          type="email"
          name={mode === "signup" ? "signup_email_field" : "existing_email_field"}
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
          className="input-exodus"
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          readOnly={mode === "existing-pin"}
        />
        {mode === "signup" && (
          <input
            data-testid="app-signup-fullname"
            type="text"
            name="fullname_signup_field"
            autoComplete="off"
            className="input-exodus"
            placeholder="Full name"
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
            required
          />
        )}
        <PinPad testid="app-signup-pin" label={mode === "signup" ? "Create a 4-digit PIN" : "Enter your PIN"}
          value={pin} onChange={setPin} />
        {mode === "signup" && (
          <PinPad testid="app-signup-pin-confirm" label="Confirm PIN" value={pinConfirm} onChange={setPinConfirm} />
        )}
      </div>

      <button
        data-testid="app-signup-submit"
        type="submit"
        disabled={loading}
        className="btn-gold w-full mt-8 flex items-center justify-center gap-3 disabled:opacity-40"
      >
        {loading ? "Just a second…" : (mode === "signup" ? "Create my access" : "Unlock the app")}
        {!loading && <ArrowRight size={14} weight="bold" />}
      </button>

      {mode === "existing-pin" && (
        <button
          type="button"
          data-testid="app-signup-back"
          onClick={() => { setMode("signup"); setPin(""); }}
          className="w-full text-xs text-exodus-muted hover:text-exodus-gold mt-4 py-2"
        >
          Use a different email
        </button>
      )}
      {mode === "existing-pin" && (
        <button type="button" data-testid="app-signup-forgot-pin" onClick={() => setMode("reset")}
          className="w-full text-xs gold-text hover:underline py-2">
          Forgot PIN?
        </button>
      )}

      <div className="mt-8 pt-6 border-t border-exodus-border flex items-start gap-3 text-xs text-exodus-muted leading-relaxed">
        <ShieldCheck size={16} className="text-exodus-gold shrink-0 mt-0.5" />
        <p>Your PIN unlocks the app on this device only. No password to remember. Need help? <a href="/support" className="gold-text hover:underline">Support &amp; contact</a> · Advisor? <a href="/login" className="gold-text hover:underline">Advisor sign-in</a></p>
      </div>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  PIN entry — returning users on a remembered device                      */
/* ═══════════════════════════════════════════════════════════════════════ */
function PinScreen({ onDone }) {
  const email = appUserAuth.email();
  const name = appUserAuth.fullName();
  const [pin, setPin] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (pin.length !== 4) return toast.error("Enter your 4-digit PIN.");
    setLoading(true);
    try {
      const r = await api.post("/app/pin-login", { email, pin });
      appUserAuth.activate(r.data.token, r.data.user.email, r.data.user.full_name);
      onDone();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      console.error("[EXODUS] pin unlock failed:", { status, detail, err });
      if (!err.response) {
        toast.error("Can't reach the server. Check your internet.");
      } else {
        toast.error(detail || "Incorrect PIN.");
      }
      setPin("");
    } finally {
      setLoading(false);
    }
  };

  // Auto-submit when 4 digits entered
  useEffect(() => { if (pin.length === 4 && !resetting) submit(); /* eslint-disable-next-line */ }, [pin]);

  if (resetting) return <ResetPin initialEmail={email} lockedEmail onDone={onDone} onCancel={() => setResetting(false)} />;

  return (
    <form onSubmit={submit} autoComplete="off" data-testid="app-pin-form" className="card-surface p-10 w-full max-w-md text-center">
      <Lock size={36} weight="thin" className="text-exodus-gold mx-auto mb-5" />
      <p className="eyebrow">EXODUS Life &amp; Wealth</p>
      <h1 className="font-display text-3xl mt-3 leading-tight">
        Welcome back{name ? `, ${name.split(" ")[0]}` : ""}.
      </h1>
      <p className="text-sm text-exodus-text/70 mt-3">Enter your PIN.</p>

      <div className="mt-8">
        <PinPad testid="app-pin-input" label="" value={pin} onChange={setPin} autoFocus centered />
      </div>

      <button
        data-testid="app-pin-submit"
        type="submit"
        disabled={loading || pin.length !== 4}
        className="btn-gold w-full mt-8 flex items-center justify-center gap-3 disabled:opacity-40"
      >
        {loading ? "Unlocking…" : "Unlock"}
        {!loading && <ArrowRight size={14} weight="bold" />}
      </button>

      <button type="button" data-testid="app-pin-forgot" onClick={() => { setPin(""); setResetting(true); }}
        className="w-full text-xs gold-text hover:underline mt-6 py-2">
        Forgot PIN?
      </button>

      <button
        type="button"
        data-testid="app-pin-forget"
        onClick={() => { appUserAuth.forget(); onDone(); }}
        className="w-full text-xs text-exodus-muted hover:text-exodus-gold mt-6 py-2"
      >
        Not you? Use a different account
      </button>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Referral helpers                                                        */
/* ═══════════════════════════════════════════════════════════════════════ */
function useReferrer() {
  const [name, setName] = useState(null);
  useEffect(() => {
    const code = localStorage.getItem("ex_ref");
    if (code) api.get(`/ref/${encodeURIComponent(code)}/info`).then((r) => setName(r.data.first_name)).catch(() => {});
  }, []);
  return name;
}

function attributeReferral() {
  const code = localStorage.getItem("ex_ref");
  if (code) api.post("/app/ref/attribute", { code }).catch(() => {}).finally(() => localStorage.removeItem("ex_ref"));
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  Forgot PIN — email a 6-digit code, then set a new PIN                   */
/* ═══════════════════════════════════════════════════════════════════════ */
function ResetPin({ initialEmail = "", lockedEmail = false, onDone, onCancel }) {
  const [email, setEmail] = useState(initialEmail || "");
  const [step, setStep] = useState("request");
  const [code, setCode] = useState("");
  const [pin, setPin] = useState("");
  const [pinConfirm, setPinConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const request = async (e) => {
    if (e) e.preventDefault();
    if (!email) return toast.error("Enter your email.");
    setLoading(true);
    try {
      await api.post("/app/pin-reset/request", { email });
      toast.success("If that email has an account, a 6-digit code is on its way.");
      setStep("confirm");
    } catch { toast.error("Could not send the code. Try again."); } finally { setLoading(false); }
  };

  const confirm = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) return toast.error("Enter the 6-digit code from your email.");
    if (!/^\d{4}$/.test(pin)) return toast.error("New PIN must be 4 digits.");
    if (pin !== pinConfirm) return toast.error("PINs don't match.");
    setLoading(true);
    try {
      const r = await api.post("/app/pin-reset/confirm", { email, code, pin });
      appUserAuth.activate(r.data.token, r.data.user.email, r.data.user.full_name);
      toast.success("PIN updated. You're in.");
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not reset your PIN.");
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={step === "request" ? request : confirm} autoComplete="off" data-testid="pin-reset-form" className="card-surface p-10 w-full max-w-md">
      <Key size={36} weight="thin" className="text-exodus-gold mb-5" />
      <p className="eyebrow">EXODUS Life &amp; Wealth</p>
      <h1 className="font-display text-3xl mt-3 leading-tight">Reset your PIN.</h1>
      <p className="text-sm text-exodus-text/70 mt-3 leading-relaxed">
        {step === "request" ? "We'll email you a 6-digit code to set a new PIN." : `Enter the code we sent to ${email}, then choose a new PIN.`}
      </p>
      <div className="mt-8 space-y-4">
        {step === "request" ? (
          <input data-testid="pin-reset-email" type="email" className="input-exodus" placeholder="Email address" autoComplete="off"
            value={email} onChange={(e) => setEmail(e.target.value)} readOnly={lockedEmail} required />
        ) : (
          <>
            <p className="text-xs eyebrow text-exodus-muted">6-digit code from your email</p>
            <input data-testid="pin-reset-code" inputMode="numeric" maxLength={6} className="input-exodus tracking-[0.5em] text-center text-xl"
              placeholder="••••••" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} />
            <PinPad testid="pin-reset-new" label="New 4-digit PIN" value={pin} onChange={setPin} />
            <PinPad testid="pin-reset-confirm" label="Confirm new PIN" value={pinConfirm} onChange={setPinConfirm} />
          </>
        )}
      </div>
      <button data-testid="pin-reset-submit" type="submit" disabled={loading} className="btn-gold w-full mt-8 flex items-center justify-center gap-3 disabled:opacity-40">
        {loading ? "Just a second…" : step === "request" ? "Email me a code" : "Set new PIN"}
      </button>
      {step === "confirm" && (
        <button type="button" data-testid="pin-reset-resend" onClick={() => request()} disabled={loading} className="w-full text-xs text-exodus-muted hover:text-exodus-gold mt-4 py-2">
          Didn't get it? Send a new code
        </button>
      )}
      <button type="button" data-testid="pin-reset-cancel" onClick={onCancel} className="w-full text-xs text-exodus-muted hover:text-exodus-gold mt-2 py-2">
        Back
      </button>
    </form>
  );
}

/* ═══════════════════════════════════════════════════════════════════════ */
/*  PIN pad — 4-digit input, numeric only, big tap targets                  */
/* ═══════════════════════════════════════════════════════════════════════ */
function PinPad({ testid, label, value, onChange, autoFocus, centered }) {
  const inputRef = useRef(null);
  useEffect(() => { if (autoFocus && inputRef.current) inputRef.current.focus(); }, [autoFocus]);

  const dots = useMemo(() => {
    return Array.from({ length: 4 }).map((_, i) => i < value.length);
  }, [value]);

  const onKey = (e) => {
    // Numeric only, max 4 chars
    const v = e.target.value.replace(/\D/g, "").slice(0, 4);
    onChange(v);
  };

  return (
    <div className={centered ? "text-center" : ""}>
      {label ? <p className="text-xs eyebrow text-exodus-muted mb-3">{label}</p> : null}
      <button
        type="button"
        onClick={() => inputRef.current?.focus()}
        className={`w-full flex items-center gap-4 py-3 ${centered ? "justify-center" : ""}`}
      >
        {dots.map((filled, i) => (
          <span
            key={i}
            data-testid={`${testid}-dot-${i}`}
            className={`w-4 h-4 rounded-full border-2 transition-colors ${
              filled ? "bg-exodus-gold border-exodus-gold" : "border-exodus-muted"
            }`}
          />
        ))}
      </button>
      <input
        ref={inputRef}
        data-testid={testid}
        type="tel"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={4}
        value={value}
        onChange={onKey}
        className="sr-only"
        aria-label={label || "PIN"}
      />
    </div>
  );
}
